/**
 * ExifTool Adapter for Image Metadata
 * 
 * Handles EXIF, IPTC, and XMP metadata for images with proper
 * round-trip safety and provenance tracking.
 */

import { CanonicalMetadata, MetadataAdapter, MetadataProvenance } from '../canonical-schema';

export class ExifAdapter implements MetadataAdapter {
  private exifTool: any;

  constructor() {
    // Initialize ExifTool only on server side
    if (typeof window === 'undefined') {
      this.initializeExifTool();
    }
  }

  private async initializeExifTool() {
    try {
      // Only import on server side
      if (typeof window === 'undefined') {
        const { exiftool } = await import('exiftool-vendored');
        this.exifTool = exiftool;
      }
    } catch (error) {
      console.warn('ExifTool not available, falling back to basic extraction');
    }
  }

  async read(file: File): Promise<CanonicalMetadata> {
    const metadata: CanonicalMetadata = {};

    if (this.exifTool) {
      return this.readWithExifTool(file);
    } else {
      return this.readBasic(file);
    }
  }

  private async readWithExifTool(file: File): Promise<CanonicalMetadata> {
    // ExifTool only works on server side, fall back to basic extraction in browser
    if (typeof window !== 'undefined') {
      return this.readBasic(file);
    }

    const buffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(buffer);
    
    try {
      // Read all metadata with ExifTool
      const tags = await this.exifTool.read(uint8Array, [
        '-P', // Preserve file modification time
        '-json', // JSON output
        '-g', // Group tags
        '-a', // All tags
        '-u' // Unknown tags
      ]);

      const metadata: CanonicalMetadata = {};
      const provenance: MetadataProvenance[] = [];

      if (tags && tags.length > 0) {
        const tag = tags[0];
        
        // Basic metadata with provenance tracking
        if (tag['EXIF:ImageDescription']) {
          metadata.description = tag['EXIF:ImageDescription'];
          provenance.push({
            source: 'exif',
            confidence: 0.9,
            timestamp: new Date(),
            software: 'ExifTool'
          });
        }
        
        if (tag['IPTC:ObjectName']) {
          metadata.title = tag['IPTC:ObjectName'];
          provenance.push({
            source: 'iptc',
            confidence: 0.8,
            timestamp: new Date(),
            software: 'ExifTool'
          });
        }
        
        if (tag['XMP:Title']) {
          metadata.title = tag['XMP:Title'];
          provenance.push({
            source: 'xmp',
            confidence: 0.9,
            timestamp: new Date(),
            software: 'ExifTool'
          });
        }

        // Creator information
        if (tag['EXIF:Artist'] || tag['IPTC:By-line'] || tag['XMP:Creator']) {
          metadata.creator = tag['XMP:Creator'] || tag['IPTC:By-line'] || tag['EXIF:Artist'];
        }

        // Copyright
        if (tag['EXIF:Copyright'] || tag['IPTC:CopyrightNotice'] || tag['XMP:Rights']) {
          metadata.copyright = tag['XMP:Rights'] || tag['IPTC:CopyrightNotice'] || tag['EXIF:Copyright'];
        }

        // Keywords
        const keywords: string[] = [];
        if (tag['IPTC:Keywords']) {
          keywords.push(...tag['IPTC:Keywords']);
        }
        if (tag['XMP:Subject']) {
          keywords.push(...tag['XMP:Subject']);
        }
        if (keywords.length > 0) {
          metadata.keywords = [...new Set(keywords)]; // Remove duplicates
        }

        // GPS information
        if (tag['EXIF:GPSLatitude'] && tag['EXIF:GPSLongitude']) {
          metadata.gps = {
            latitude: this.parseGPSCoordinate(tag['EXIF:GPSLatitude']),
            longitude: this.parseGPSCoordinate(tag['EXIF:GPSLongitude']),
            altitude: tag['EXIF:GPSAltitude'] ? parseFloat(tag['EXIF:GPSAltitude']) : undefined
          };
        }

        // Rating (XMP)
        if (tag['XMP:Rating']) {
          metadata.rating = parseInt(tag['XMP:Rating']);
        }

        // Color label (XMP)
        if (tag['XMP:Label']) {
          const labelMap: Record<string, 'Red' | 'Yellow' | 'Green' | 'Blue' | 'Purple'> = {
            'Red': 'Red',
            'Yellow': 'Yellow', 
            'Green': 'Green',
            'Blue': 'Blue',
            'Purple': 'Purple'
          };
          metadata.colorLabel = labelMap[tag['XMP:Label']];
        }

        // Technical metadata
        if (tag['EXIF:ImageWidth'] && tag['EXIF:ImageHeight']) {
          metadata.technical = {
            resolution: {
              width: parseInt(tag['EXIF:ImageWidth']),
              height: parseInt(tag['EXIF:ImageHeight'])
            },
            colorSpace: tag['EXIF:ColorSpace'] === '1' ? 'sRGB' : tag['EXIF:ColorSpace'],
            colorDepth: tag['EXIF:BitsPerSample'] ? parseInt(tag['EXIF:BitsPerSample']) : undefined
          };
        }

        // Provenance
        metadata.provenance = {
          created: tag['EXIF:DateTime'] ? new Date(tag['EXIF:DateTime']) : undefined,
          modified: tag['EXIF:DateTimeOriginal'] ? new Date(tag['EXIF:DateTimeOriginal']) : undefined,
          software: tag['EXIF:Software'] || tag['XMP:CreatorTool'],
          version: tag['EXIF:SoftwareVersion']
        };
      }

      return metadata;
    } catch (error) {
      console.error('ExifTool read error:', error);
      return this.readBasic(file);
    }
  }

  private async readBasic(file: File): Promise<CanonicalMetadata> {
    // Fallback basic extraction for when ExifTool is not available
    const metadata: CanonicalMetadata = {};

    if (file.type.startsWith('image/')) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            metadata.technical = {
              resolution: {
                width: img.width,
                height: img.height
              }
            };
            metadata.title = file.name.replace(/\.[^/.]+$/, "");
            resolve(metadata);
          };
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      });
    }

    return metadata;
  }

  async write(file: File, metadata: CanonicalMetadata): Promise<File> {
    if (!this.exifTool || typeof window !== 'undefined') {
      throw new Error('ExifTool not available for writing metadata in browser environment');
    }

    const buffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(buffer);
    
    try {
      // Prepare ExifTool arguments
      const args: string[] = ['-P', '-overwrite_original']; // Preserve mtime, overwrite original
      
      // Map canonical metadata to ExifTool tags
      if (metadata.title) {
        args.push(`-IPTC:ObjectName=${metadata.title}`);
        args.push(`-XMP:Title=${metadata.title}`);
      }
      
      if (metadata.description) {
        args.push(`-EXIF:ImageDescription=${metadata.description}`);
        args.push(`-XMP:Description=${metadata.description}`);
      }
      
      if (metadata.creator) {
        args.push(`-EXIF:Artist=${metadata.creator}`);
        args.push(`-IPTC:By-line=${metadata.creator}`);
        args.push(`-XMP:Creator=${metadata.creator}`);
      }
      
      if (metadata.copyright) {
        args.push(`-EXIF:Copyright=${metadata.copyright}`);
        args.push(`-IPTC:CopyrightNotice=${metadata.copyright}`);
        args.push(`-XMP:Rights=${metadata.copyright}`);
      }
      
      if (metadata.keywords && metadata.keywords.length > 0) {
        args.push(`-IPTC:Keywords=${metadata.keywords.join(',')}`);
        args.push(`-XMP:Subject=${metadata.keywords.join(',')}`);
      }
      
      if (metadata.rating !== undefined) {
        args.push(`-XMP:Rating=${metadata.rating}`);
      }
      
      if (metadata.colorLabel) {
        args.push(`-XMP:Label=${metadata.colorLabel}`);
      }
      
      if (metadata.gps) {
        if (metadata.gps.latitude !== undefined) {
          args.push(`-EXIF:GPSLatitude=${metadata.gps.latitude}`);
        }
        if (metadata.gps.longitude !== undefined) {
          args.push(`-EXIF:GPSLongitude=${metadata.gps.longitude}`);
        }
        if (metadata.gps.altitude !== undefined) {
          args.push(`-EXIF:GPSAltitude=${metadata.gps.altitude}`);
        }
      }

      // Write metadata
      const result = await this.exifTool.write(uint8Array, args);
      
      // Create new file with updated metadata
      const blob = new Blob([result], { type: file.type });
      return new File([blob], file.name, { 
        type: file.type,
        lastModified: file.lastModified // Preserve original timestamp
      });
    } catch (error) {
      console.error('ExifTool write error:', error);
      throw new Error('Failed to write metadata to file');
    }
  }

  getSupportedExtensions(): string[] {
    return [
      '.jpg', '.jpeg', '.tiff', '.tif', '.png', '.gif', '.bmp', '.webp',
      '.cr2', '.nef', '.arw', '.dng', '.orf', '.rw2', '.pef', '.srw',
      '.raf', '.x3f', '.mrw', '.mef', '.mos', '.dcr', '.kdc', '.erf'
    ];
  }

  supports(file: File): boolean {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    return this.getSupportedExtensions().includes(extension);
  }

  private parseGPSCoordinate(coord: string): number {
    // Parse GPS coordinate from ExifTool format (e.g., "40 deg 44' 54.36\" N")
    const match = coord.match(/(\d+(?:\.\d+)?)\s*deg\s*(\d+(?:\.\d+)?)\s*'\s*(\d+(?:\.\d+)?)\s*"/);
    if (match) {
      const degrees = parseFloat(match[1]);
      const minutes = parseFloat(match[2]);
      const seconds = parseFloat(match[3]);
      return degrees + minutes / 60 + seconds / 3600;
    }
    return parseFloat(coord) || 0;
  }
}
